import type { Product } from '../../../catalog/domain-api';
import type { StockRow } from '../../../inventory/stock/public-api';
import { useInventoryBulkPhysicalFlow } from './useInventoryBulkPhysicalFlow';
import { useInventoryHistoryFlow } from './useInventoryHistoryFlow';
import { useInventoryMutationsFlow } from './useInventoryMutationsFlow';
import { useInventoryTransferFlow } from './useInventoryTransferFlow';
import type { InventoryRefreshHandlers } from '../../application/inventoryFlowBridge';

type Context = ReturnType<typeof import('./useInventoryScreenContext').useInventoryScreenContext>;

type Params = {
  ctx: Pick<Context, 'currentBranchId' | 'user' | 'canEdit' | 'canAdjust' | 'canTransfer' | 'canCount' | 'canViewValuation' | 'destinationBranches' | 'stockHistoryRemoteOptions' | 'currentBranchName' | 'actorLabel'>;
  stocks: StockRow[];
  filteredStocks: StockRow[];
  loadStocks: () => Promise<void>;
  loadPendingTransfers: () => Promise<void>;
  loadBranchActivity: () => Promise<void>;
  loadInventoryDashboard: () => Promise<void>;
  refreshKpis: () => Promise<void>;
  closeAdjustModal: () => void;
  closeCostCorrectModal: () => void;
  closeAddStockModal: () => void;
  closeActivityModal: () => void;
  openAddStockModalForProduct: (product: Product) => void;
  selectedProduct: Product | null;
  entryQuantity: string;
  entryUnitCost: string;
  entryMinQuantity: string;
  entryExpiry: string;
  entryReason: 'purchase_reception' | 'stock_restock' | 'stock_initial_load';
  entryLotNumber: string;
  entrySupplierId: string | null;
  entryVariantId: string | null;
  setSavingEntry: (v: boolean) => void;
};

/** Orquestación de flujos de inventario (bridge sobre InventoryOperationsController). */
export function useInventoryFlows(params: Params) {
  const refresh: InventoryRefreshHandlers = {
    loadStocks: params.loadStocks,
    loadPendingTransfers: params.loadPendingTransfers,
    loadBranchActivity: params.loadBranchActivity,
    loadInventoryDashboard: params.loadInventoryDashboard,
    refreshKpis: params.refreshKpis,
  };

  const transferFlow = useInventoryTransferFlow({ ctx: params.ctx, refresh });
  const bulkPhysical = useInventoryBulkPhysicalFlow({ ctx: params.ctx, refresh });
  const history = useInventoryHistoryFlow({ ctx: params.ctx });
  const mutations = useInventoryMutationsFlow({
    ctx: params.ctx,
    stocks: params.stocks,
    filteredStocks: params.filteredStocks,
    refresh,
    closeAdjustModal: params.closeAdjustModal,
    closeCostCorrectModal: params.closeCostCorrectModal,
    closeAddStockModal: params.closeAddStockModal,
    closeActivityModal: params.closeActivityModal,
    openAddStockModalForProduct: params.openAddStockModalForProduct,
    selectedProduct: params.selectedProduct,
    entryQuantity: params.entryQuantity,
    entryUnitCost: params.entryUnitCost,
    entryMinQuantity: params.entryMinQuantity,
    entryExpiry: params.entryExpiry,
    entryReason: params.entryReason,
    entryLotNumber: params.entryLotNumber,
    entrySupplierId: params.entrySupplierId,
    entryVariantId: params.entryVariantId,
    setSavingEntry: params.setSavingEntry,
  });

  return {
    transferFlow,
    ...bulkPhysical,
    ...history,
    ...mutations,
  };
}
